import { isApolloError } from '@apollo/client';
import { isTRPCClientError } from '@trpc/client';
import axios from 'axios';
import type { UseFormSetError } from 'react-hook-form';

import { getErrorMessage } from '@/utils/errorUtils';

type FormErrorField = 'global' | 'newFiles';

type MappedError =
  | { kind: 'suppress' }
  | { kind: 'field'; field: FormErrorField; message: string; type?: string }
  | { kind: 'unknown' };

const messageMatchesChangeRequest = (e: Error): boolean =>
  isApolloError(e) &&
  e.graphQLErrors.some((er) =>
    er.message.includes(
      'You need to create a change request to perform this action.'
    )
  );

const messageMatchesAttestationWarning = (e: Error): boolean =>
  e.message.includes('attestation warning error');

export const mapFormError = (error: unknown): MappedError => {
  if (!(error instanceof Error)) {
    return { kind: 'unknown' };
  }

  if (
    messageMatchesChangeRequest(error) ||
    messageMatchesAttestationWarning(error)
  ) {
    return { kind: 'suppress' };
  }

  if (axios.isAxiosError(error) && error.config?.url?.startsWith('/files')) {
    return {
      kind: 'field',
      field: 'newFiles',
      message: getErrorMessage(error) || 'Unable to upload files',
    };
  }

  if (isApolloError(error)) {
    const code = error.graphQLErrors?.[0]?.extensions?.code;

    return {
      kind: 'field',
      field: 'global',
      message: error.message,
      type: typeof code === 'string' ? code : undefined,
    };
  }

  if (isTRPCClientError(error)) {
    return {
      kind: 'field',
      field: 'global',
      message: error.message,
      type: error.data?.code,
    };
  }

  return { kind: 'unknown' };
};

/**
 * react-hook-form's `setError` is typed against the form schema's `FieldPath`,
 * which doesn't include the cross-form sentinels `global` / `newFiles` that
 * `FormErrors` reads. Cast at this single boundary instead of `@ts-ignore` at
 * every call site.
 */
export const setFormFieldError = <TFieldValues extends Record<string, unknown>>(
  setError: UseFormSetError<TFieldValues>,
  field: FormErrorField,
  error: { message: string; type?: string }
): void => {
  (setError as unknown as (name: string, e: typeof error) => void)(
    field,
    error
  );
};
